<template>
  <div class="grid crud-demo">
    <div class="col-12">
      <div class="card">
        <Toast />
        <Toolbar class="mb-4">
          <template #start>
            <div class="my-2">
              <Button
                label="New Group"
                icon="pi pi-plus"
                class="p-button-success mr-2"
                @click="openNew"
              />
              <Button
                label="Delete"
                icon="pi pi-trash"
                class="p-button-danger"
                :disabled="!selectedGroups || !selectedGroups.length"
                @click="confirmDeleteSelected"
              />
            </div>
          </template>
        </Toolbar>

        <DataTable
          ref="dt"
          v-model:selection="selectedGroups"
          :value="groups"
          data-key="id"
          :paginator="true"
          :rows="10"
          :filters="filters"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rows-per-page-options="[5, 10, 25]"
          current-page-report-template="Showing {first} to {last} of {totalRecords} groups"
          :loading="loading"
        >
          <template #header>
            <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
              <h5 class="m-0">
                Manage Question Groups
              </h5>
              <IconField class="block mt-2 md:mt-0">
                <InputIcon class="pi pi-search" />
                <InputText
                  v-model="filters.global.value"
                  placeholder="Search..."
                />
              </IconField>
            </div>
          </template>

          <Column
            selection-mode="multiple"
            header-style="width: 3rem"
          />
          <Column
            field="id"
            header="ID"
            :sortable="true"
          >
            <template #body="slotProps">
              <span class="p-column-title">ID</span>
              {{ slotProps.data.id }}
            </template>
          </Column>
          <Column
            field="title"
            header="Title"
            :sortable="true"
          >
            <template #body="slotProps">
              <span class="p-column-title">Title</span>
              {{ slotProps.data.title }}
            </template>
          </Column>
          <Column
            field="created_at"
            header="Created At"
            :sortable="true"
          >
            <template #body="slotProps">
              <span class="p-column-title">Created At</span>
              {{ formatDate(slotProps.data.created_at) }}
            </template>
          </Column>
          <Column>
            <template #body="slotProps">
              <Button
                icon="pi pi-pencil"
                class="p-button-rounded p-button-success mr-2"
                @click="editGroup(slotProps.data)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-warn"
                @click="confirmDeleteGroup(slotProps.data)"
              />
            </template>
          </Column>
        </DataTable>

        <Dialog
          v-model:visible="groupDialog"
          :style="{ width: '450px' }"
          header="Group Details"
          :modal="true"
        >
          <div class="field">
            <label for="title">Title</label>
            <InputText
              id="title"
              v-model.trim="group.title"
              required="true"
              autofocus
              fluid
              :invalid="submitted && !group.title"
            />
            <small
              v-if="submitted && !group.title"
              class="p-invalid"
            >Name is required.</small>
          </div>
          <template #footer>
            <Button
              label="Cancel"
              icon="pi pi-times"
              class="p-button-text"
              @click="hideDialog"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveGroup"
            />
          </template>
        </Dialog>

        <Dialog
          v-model:visible="deleteGroupDialog"
          :style="{ width: '450px' }"
          header="Confirm"
          :modal="true"
        >
          <div class="flex align-items-center justify-content-center">
            <i
              class="pi pi-exclamation-triangle mr-3"
              style="font-size: 2rem"
            />
            <span v-if="group">Are you sure you want to delete <b>{{ group.title }}</b>?</span>
          </div>
          <template #footer>
            <Button
              label="No"
              icon="pi pi-times"
              class="p-button-text"
              @click="deleteGroupDialog = false"
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              class="p-button-text"
              @click="deleteGroup"
            />
          </template>
        </Dialog>

        <Dialog
          v-model:visible="deleteGroupsDialog"
          :style="{ width: '450px' }"
          header="Confirm"
          :modal="true"
        >
          <div class="flex align-items-center justify-content-center">
            <i
              class="pi pi-exclamation-triangle mr-3"
              style="font-size: 2rem"
            />
            <span>Are you sure you want to delete the selected groups?</span>
          </div>
          <template #footer>
            <Button
              label="No"
              icon="pi pi-times"
              class="p-button-text"
              @click="deleteGroupsDialog = false"
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              class="p-button-text"
              @click="deleteSelectedGroups"
            />
          </template>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script>
import { FilterMatchMode } from '@primevue/core/api'
import { mapActions, mapState } from 'vuex'
import { useToast } from 'primevue/usetoast'

export default {
  setup () {
    const toast = useToast()
    return { toast }
  },
  data () {
    return {
      groupDialog: false,
      deleteGroupDialog: false,
      deleteGroupsDialog: false,
      group: {},
      selectedGroups: null,
      filters: {},
      submitted: false
    }
  },
  computed: {
    ...mapState({
      groups: state => state.groups.items,
      loading: state => state.groups.loading
    })
  },
  created () {
    this.initFilters()
  },
  mounted () {
    this.fetchGroups()
  },
  methods: {
    ...mapActions({
      fetchGroups: 'groups/getGroups',
      createGroup: 'groups/createGroup',
      updateGroup: 'groups/updateGroup',
      removeGroup: 'groups/deleteGroup'
    }),
    formatDate (value) {
      if (value) {
        const date = new Date(value)
        return date.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })
      }
      return ''
    },
    openNew () {
      this.group = {}
      this.submitted = false
      this.groupDialog = true
    },
    hideDialog () {
      this.groupDialog = false
      this.submitted = false
    },
    saveGroup () {
      this.submitted = true

      if (this.group.title?.trim()) {
        if (this.group.id) {
          // Update existing group
          this.updateGroup({ groupId: this.group.id, data: this.group })
            .then(() => {
              this.toast.add({ severity: 'success', summary: 'Successful', detail: 'Group Updated', life: 3000 })
              this.groupDialog = false
            })
            .catch(error => {
              this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to update group', life: 3000 })
              console.error('Failed to update group:', error)
            })
        } else {
          // Create new group
          this.createGroup(this.group)
            .then(() => {
              this.toast.add({ severity: 'success', summary: 'Successful', detail: 'Group Created', life: 3000 })
              this.groupDialog = false
            })
            .catch(error => {
              this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to create group', life: 3000 })
              console.error('Failed to create group:', error)
            })
        }
      }
    },
    editGroup (group) {
      this.group = { ...group }
      this.groupDialog = true
    },
    confirmDeleteGroup (group) {
      this.group = group
      this.deleteGroupDialog = true
    },
    deleteGroup () {
      this.removeGroup(this.group.id)
        .then(() => {
          this.deleteGroupDialog = false
          this.group = {}
          this.toast.add({ severity: 'success', summary: 'Successful', detail: 'Group Deleted', life: 3000 })
        })
        .catch(error => {
          this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete group', life: 3000 })
          console.error('Failed to delete group:', error)
        })
    },
    confirmDeleteSelected () {
      this.deleteGroupsDialog = true
    },
    deleteSelectedGroups () {
      const deletePromises = this.selectedGroups.map(group => this.removeGroup(group.id))

      Promise.all(deletePromises)
        .then(() => {
          this.deleteGroupsDialog = false
          this.selectedGroups = null
          this.toast.add({ severity: 'success', summary: 'Successful', detail: 'Groups Deleted', life: 3000 })
        })
        .catch(error => {
          this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete some groups', life: 3000 })
          console.error('Failed to delete groups:', error)
        })
    },
    initFilters () {
      this.filters = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
      }
    }
  }
}
</script>
